import React, { useState } from "react";
import { Card, Dropdown } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import RecipeUsageForm from "./RecipeUsageForm";
import ConfirmationPopup from "components/common/ConfirmationPopup";
import RecipeUsageService from "services/RecipeUsageService";
import {
  RECIPE_USAGE_DELETE_ERROR,
  ARE_YOU_SURE_DELETE_RECIPE_USAGE,
  RECIPE_USAGE_HAS_BEEN_DELETED_SUCCESSFULLY,
} from "configurations/MessageConstants";
import {
  setEditedRecipeUsage,
  setRecipeUsages,
} from "reduxStore/slices/recipeUsageSlice";
import Notes from "components/common/notes/Notes";
import { MODE_TYPES, NOTES_TYPES } from "configurations/Constants";
import { toast } from "react-toastify";

const RecipeUsageCard = ({
  recipeUsage,
  onEditRecipeUsage,
  onDeleteRecipeUsage,
}) => {
  const dispatch = useDispatch();
  const recipeUsages = useSelector((state) => state.recipeUsage.recipeUsages);
  const [showRecipeUsageForm, setShowRecipeUsageForm] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const selectedSite = useSelector((state) => state.site.selectedSite);
  const selectedPlantedAcre = useSelector(
    (state) => state.plantedAcres.selectedPlantedAcre
  );
  const isSharedViewOnly = selectedSite.isShared && !selectedSite.editor;

  if (!recipeUsage) {
    return null;
  }

  const handleDeleteClick = () => {
    if (onDeleteRecipeUsage) {
      onDeleteRecipeUsage(recipeUsage);
    } else {
      setShowDeleteConfirmation(true);
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await RecipeUsageService.deleteRecipeUsage(
        selectedSite.id,
        selectedPlantedAcre.id,
        recipeUsage.id
      );
      setShowDeleteConfirmation(false);
      dispatch(
        setRecipeUsages(
          recipeUsages.filter((usage) => usage.id !== recipeUsage.id)
        )
      );
      toast.success(RECIPE_USAGE_HAS_BEEN_DELETED_SUCCESSFULLY);
    } catch (error) {
      console.error(RECIPE_USAGE_DELETE_ERROR, error);
      toast.error(error.response.data.detail);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditClick = () => {
    dispatch(setEditedRecipeUsage(recipeUsage));
    if (onEditRecipeUsage) {
      onEditRecipeUsage(recipeUsage);
    } else {
      setShowRecipeUsageForm(true);
    }
  };

  const handleShowNotes = () => {
    setShowNotes(true);
  };

  const handleClose = () => {
    dispatch(setEditedRecipeUsage(null));
    setShowRecipeUsageForm(false);
  };

  return (
    <>
      <Card className="recipe-usage-card">
        <Card.Body className="p-2">
          <div className="d-flex justify-content-between">
            <div className="text-truncate">
              <div className="text-truncate">{recipeUsage.recipeName}</div>
              <small className="text-muted">
                Total: {recipeUsage.totalAcres} Acres | Sprayed:{" "}
                {recipeUsage.sprayedAcres} Acres
              </small>
            </div>
            <div className="ms-2 d-flex flex-column align-items-end">
              <small className="text-muted text-nowrap">
                {recipeUsage.quantity} G/A
              </small>
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <div
              className={`text-muted recipe-usage-badge ${recipeUsage.status.toLowerCase()}`}
            >
              <small>
                {recipeUsage.status} - {recipeUsage.applicationDate}
              </small>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="outline-dark"
                  className="no-caret"
                  size="sm"
                  id={`dropdown-${recipeUsage.id}`}
                  aria-label="Recipe Usage actions"
                >
                  <i className="fa fa-ellipsis-h" aria-hidden="true"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ minWidth: "fit-content" }}>
                  <Dropdown.Item onClick={handleEditClick}>
                    {isSharedViewOnly ? (
                      <>
                        <i className="fa fa-eye me-2" aria-hidden="true"></i>
                        View
                      </>
                    ) : (
                      <>
                        <i className="fa fa-edit me-2" aria-hidden="true"></i>
                        Edit
                      </>
                    )}
                  </Dropdown.Item>
                  {!isSharedViewOnly && (
                    <>
                      <Dropdown.Item onClick={handleShowNotes}>
                        <i
                          className="fa fa-sticky-note me-2"
                          aria-hidden="true"
                        ></i>
                        Notes
                      </Dropdown.Item>
                      <Dropdown.Item onClick={handleDeleteClick}>
                        <i className="fa fa-trash me-2" aria-hidden="true"></i>
                        Delete
                      </Dropdown.Item>
                    </>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Card.Body>
      </Card>

      {!onEditRecipeUsage && (
        <RecipeUsageForm
          mode={MODE_TYPES.EDIT}
          show={showRecipeUsageForm}
          onClose={handleClose}
        />
      )}

      {!onDeleteRecipeUsage && (
        <ConfirmationPopup
          show={showDeleteConfirmation}
          title="Delete Recipe Usage"
          message={
            <span className="text-muted">
              {ARE_YOU_SURE_DELETE_RECIPE_USAGE}?
            </span>
          }
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteConfirmation(false)}
          confirmText="Delete"
          cancelText="Cancel"
          isDeleting={isDeleting}
        />
      )}
      {showNotes && (
        <Notes
          id={recipeUsage.id}
          type={NOTES_TYPES.USAGE}
          name={recipeUsage.recipeName}
          show={showNotes}
          handleClose={() => setShowNotes(false)}
        />
      )}
    </>
  );
};

export default RecipeUsageCard;
